import React, { useEffect, useMemo, useState } from 'react';
import {
  Archive,
  ChevronDown,
  FileCheck2,
  FileSpreadsheet,
  FileText,
  KeyRound,
  LogOut,
  PackagePlus,
  RefreshCw,
  Search,
  Trash2,
  Users,
  X,
} from 'lucide-react';

import { ovhApi } from './lib/ovhApi';
import { computedStatus, statusRank } from './lib/status';
import {
  parsePodPdf,
  parseStefPdf,
  parseTrackAndTrace,
} from './lib/importers';
import { scorePodCandidate } from './lib/podParser';
import {
  getPasswordSetupReasonFromUrl,
  getPasswordSetupTokenFromUrl,
} from './lib/authRedirects';
import {
  ChangePasswordModal,
  RecoveryPasswordPage,
} from './components/PasswordForms';
import UserManagement from './components/UserManagement';

const formatDate = (value) => {
  if (!value) return '—';

  const dateValue = String(value).slice(0, 10);
  const [year, month, day] = dateValue.split('-').map(Number);

  if (!year || !month || !day) return '—';

  return new Intl.DateTimeFormat('fr-FR').format(
    new Date(year, month - 1, day)
  );
};

const getTodayValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const safe = (value) => value || '—';

const formatDeliveryDate = (shipment) =>
  shipment.actual_delivery_date
    ? `Livrée le ${formatDate(shipment.actual_delivery_date)}`
    : `Estimée le ${formatDate(shipment.estimated_delivery_date)}`;

const normalizeComparison = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

const matchesImportedLdv = (storedValue, parsedValue) => {
  const stored = String(storedValue ?? '').replace(/\s+/g, '').toUpperCase();
  const parsed = String(parsedValue ?? '').replace(/\s+/g, '').toUpperCase();

  if (stored === parsed) return true;

  /*
   * Répare les LDV créées par l'ancien parseur qui pouvait ajouter un ou
   * plusieurs chiffres de la colonne palettes après les 10 chiffres STEF.
   */
  return (
    /^0V\d{10}$/.test(parsed) &&
    /^0V\d{11,13}$/.test(stored) &&
    stored.startsWith(parsed)
  );
};

const numbersMatch = (first, second) => {
  if (first === null || first === undefined || first === '') return false;
  if (second === null || second === undefined || second === '') return false;
  return Number(first) === Number(second);
};

async function sha256File(file) {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    await file.arrayBuffer()
  );

  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
}

function buildTrackPayload(row, completedAt = null) {
  const payload = {
    track_status_raw: row.trackStatus,
    last_track_import_at: new Date().toISOString(),
  };

  if (row.conformity) {
    payload.conformity = row.conformity;
  }

  if (row.finished) {
    payload.status = 'completed';
    payload.completed_at = completedAt || new Date().toISOString();

    if (row.actualDate) {
      payload.actual_delivery_date = row.actualDate;
    }
  }

  return payload;
}

function scoreLdvCandidate(shipment, row) {
  const reasons = [
    'Même numéro de BL',
    'Même code transporteur',
  ];
  let score = 6;

  if (
    row.city &&
    normalizeComparison(shipment.city) === normalizeComparison(row.city)
  ) {
    score += 3;
    reasons.push('Même ville');
  }

  if (numbersMatch(shipment.weight_kg, row.weightKg)) {
    score += 2;
    reasons.push('Même poids');
  }

  if (numbersMatch(shipment.parcel_count, row.parcelCount)) {
    score += 1;
    reasons.push('Même nombre de colis');
  }

  if (numbersMatch(shipment.pallet_count, row.palletCount)) {
    score += 1;
    reasons.push('Même nombre de palettes');
  }

  const currentRecipient = normalizeComparison(shipment.recipient);
  const trackRecipient = normalizeComparison(row.recipient);

  if (
    currentRecipient &&
    trackRecipient &&
    (
      currentRecipient.includes(trackRecipient) ||
      trackRecipient.includes(currentRecipient)
    )
  ) {
    score += 1;
    reasons.push('Destinataire correspondant');
  }

  return { score, reasons };
}


function Badge({ shipment }) {
  const status = computedStatus(shipment);

  return (
    <span className={`badge badge-${status.key}`}>
      <span className="badge-dot" />
      {status.label}
    </span>
  );
}

function ConformityBadge({ value }) {
  if (!value) return <span className="muted">—</span>;

  const isCompliant = value === 'compliant';
  const isAnomaly = value === 'anomaly';

  return (
    <span
      className={`badge ${
        isCompliant
          ? 'badge-compliant'
          : isAnomaly
            ? 'badge-anomaly'
            : 'badge-non_compliant'
      }`}
    >
      <span className="badge-dot" />
      {isCompliant ? 'Conforme' : isAnomaly ? 'Anomalie' : 'Non conforme'}
    </span>
  );
}

function MobileShipmentCard({
  shipment,
  isStaff,
  archived,
  selected,
  onToggle,
  onRemove,
}) {
  const status = computedStatus(shipment);
  const mobileStatusLabel = status.key === 'in_delivery'
    ? 'En livraison'
    : status.label;
  const palletCount = Number(shipment.pallet_count || 0);

  return (
    <details className="mobile-shipment-card">
      <summary>
        <div className="mobile-summary-top">
          <span className={`badge badge-${status.key}`}>
            <span className="badge-dot" />
            {mobileStatusLabel}
          </span>

          <strong>{safe(shipment.customer_code)}</strong>
          <span className="mobile-city">
            {safe(shipment.city)}
          </span>
          <ChevronDown className="mobile-chevron" size={19} />
        </div>

        <span className="mobile-summary-meta">
          <span className="mobile-recipient">
            {safe(shipment.recipient)}
          </span>
          <span>{formatDeliveryDate(shipment)}</span>
        </span>
      </summary>

      <div className="mobile-shipment-details">
        <dl>
          <div className="mobile-detail-wide">
            <dt>Date de livraison</dt>
            <dd>{formatDeliveryDate(shipment)}</dd>
          </div>

          <div>
            <dt>Nombre d’EUR</dt>
            <dd>
              {palletCount || safe(shipment.pallet_count)}{' '}
              {palletCount > 1 ? 'palettes' : 'palette'}
            </dd>
          </div>

          <div>
            <dt>BL</dt>
            <dd>{safe(shipment.bl_number)}</dd>
          </div>

          <div>
            <dt>LDV</dt>
            <dd>{safe(shipment.ldv_number)}</dd>
          </div>

          <div>
            <dt>Date d’expédition</dt>
            <dd>{formatDate(shipment.shipping_date)}</dd>
          </div>

          <div className="mobile-detail-wide">
            <dt>Conformité</dt>
            <dd><ConformityBadge value={shipment.conformity} /></dd>
          </div>
        </dl>

        {isStaff && (
          <div className="mobile-card-actions">
            {!archived && (
              <label>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={onToggle}
                />
                Sélectionner cette expédition
              </label>
            )}

            <button
              type="button"
              className="danger-text"
              onClick={onRemove}
            >
              <Trash2 size={16} />
              Supprimer
            </button>
          </div>
        )}
      </div>
    </details>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setMessage('');
    setSubmitting(true);

    try {
      const user = await ovhApi.login(email.trim(), password);
      onLogin(user);
    } catch (error) {
      setMessage(
        error?.payload?.code === 'account_suspended'
          ? 'Votre compte a été suspendu. Veuillez contacter votre administration.'
          : 'Adresse email ou mot de passe incorrect.'
      );
    }

    setSubmitting(false);
  };

  const reset = async () => {
    if (!email.trim()) {
      setMessage('Saisis ton adresse email.');
      return;
    }

    setMessage('');

    try {
      const result = await ovhApi.forgotPassword(email.trim());
      setMessage(result.message);
    } catch {
      setMessage(
        `Si l’adresse ${email.trim()} est connue de nos services, ` +
          'un lien de réinitialisation lui a été envoyé.'
      );
    }
  };

  return (
    <div className="login-shell">
      <form className="panel login" onSubmit={submit}>
        <div className="login-brand">
          <div className="login-logo">G&amp;F</div>

          <div>
            <h1>Suivi des expéditions</h1>
            <p>Geoffroy et Fils</p>
          </div>
        </div>

        <div className="login-introduction">
          Connectez-vous pour consulter le suivi des livraisons.
        </div>

        <label>
          Adresse email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </label>

        <label>
          Mot de passe
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <button
          type="submit"
          className="primary"
          disabled={submitting}
        >
          {submitting ? 'Connexion…' : 'Se connecter'}
        </button>

        <button type="button" className="link" onClick={reset}>
          Mot de passe oublié
        </button>

        {message && <div className="message">{message}</div>}
      </form>
    </div>
  );
}

function CreateShipmentModal({
  open,
  onClose,
  onCreate,
  submitting,
}) {
  const [customerCode, setCustomerCode] = useState('');
  const [shippingDate, setShippingDate] = useState(getTodayValue());

  useEffect(() => {
    if (!open) return;

    setCustomerCode('');
    setShippingDate(getTodayValue());
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();

    const success = await onCreate({
      customerCode: customerCode.trim(),
      shippingDate,
    });

    if (success) onClose();
  };

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-shipment-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 id="create-shipment-title">
              Nouvelle expédition
            </h2>

            <p>
              Planifier une expédition en attente de préparation.
            </p>
          </div>

          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Fermer"
          >
            <X size={19} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <label>
              Code client
              <input
                type="text"
                value={customerCode}
                onChange={(event) =>
                  setCustomerCode(event.target.value.toUpperCase())
                }
                placeholder="Ex. SUPRAV"
                autoFocus
                required
              />
            </label>

            <label>
              Date d’expédition
              <input
                type="date"
                value={shippingDate}
                onChange={(event) =>
                  setShippingDate(event.target.value)
                }
                required
              />
            </label>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Annuler
            </button>

            <button
              type="submit"
              className="primary modal-primary"
              disabled={submitting}
            >
              {submitting ? 'Création…' : 'Créer l’expédition'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}


function LdvChangeModal({
  suggestions,
  resolving,
  onConfirm,
  onIgnore,
}) {
  const suggestion = suggestions[0];

  if (!suggestion) return null;

  const { shipment, row, reasons, source } = suggestion;
  const isPod = source === 'pod';

  return (
    <div className="modal-backdrop">
      <section
        className="modal ldv-change-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ldv-change-title"
      >
        <div className="modal-header">
          <div>
            <h2 id="ldv-change-title">
              Changement de LDV probable
            </h2>
            <p>
              {isPod
                ? 'Ce POD semble correspondre à une expédition existante.'
                : 'Une ligne du Track & Trace semble correspondre à une expédition existante.'}
            </p>
          </div>
        </div>

        <div className="modal-content">
          <div className="ldv-customer">
            <strong>
              {safe(shipment.customer_code)} · {safe(shipment.recipient)}
            </strong>
            <span>{safe(shipment.city)}</span>
          </div>

          <div className="ldv-comparison">
            <div>
              <span>Informations actuelles</span>
              <strong>{safe(shipment.ldv_number)}</strong>
              <small>
                Expédition : {formatDate(shipment.shipping_date)}
              </small>
            </div>

            <div className="ldv-arrow" aria-hidden="true">→</div>

            <div>
              <span>Nouvelles informations transporteur</span>
              <strong>{safe(row.ldv)}</strong>
              <small>
                {isPod ? 'POD remis' : 'Remise'} :{' '}
                {formatDate(row.shippingDate)}
              </small>
            </div>
          </div>

          <div className="ldv-evidence">
            <strong>Éléments concordants</strong>
            <ul>
              {reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>

          {suggestions.length > 1 && (
            <p className="ldv-remaining">
              {suggestions.length - 1} autre(s) proposition(s) à vérifier
              après celle-ci.
            </p>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="secondary"
            onClick={onIgnore}
            disabled={resolving}
          >
            Ignorer cette proposition
          </button>

          <button
            type="button"
            className="primary modal-primary"
            onClick={() => onConfirm(suggestion)}
            disabled={resolving}
          >
            {resolving
              ? 'Mise à jour…'
              : isPod
                ? 'Confirmer ce POD'
                : 'Confirmer le changement de LDV'}
          </button>
        </div>
      </section>
    </div>
  );
}

function StatCard({ value, label, statusClass, active }) {
  return (
    <article className={`stat-card ${statusClass} ${active ? 'active' : ''}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </article>
  );
}

function App() {
  const [session, setSession] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [profile, setProfile] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [passwordSetup, setPasswordSetup] = useState(() => {
    const type = getPasswordSetupReasonFromUrl();
    const token = getPasswordSetupTokenFromUrl();
    return type && token ? { type, token } : null;
  });

  const [archived, setArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [commercial, setCommercial] = useState('');
  const [sector, setSector] = useState('');
  const [selectedShipmentIds, setSelectedShipmentIds] = useState(
    () => new Set()
  );
  const [usersView, setUsersView] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatingShipment, setCreatingShipment] = useState(false);
  const [ldvSuggestions, setLdvSuggestions] = useState([]);
  const [resolvingLdvChange, setResolvingLdvChange] = useState(false);

  useEffect(() => {
    let active = true;

    ovhApi
      .session()
      .then((user) => {
        if (active) setSession(user ? { user } : null);
      })
      .catch(() => {
        if (active) setSession(null);
      })
      .finally(() => {
        if (active) setSessionChecked(true);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setSelectedShipmentIds(new Set());
  }, [archived]);

  useEffect(() => {
    setSelectedShipmentIds(new Set());
  }, [search, commercial, sector]);

  useEffect(() => {
    setUsersView(false);

    if (session?.user?.id) {
      setArchived(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (!session) {
      setProfile(null);
      setShipments([]);
      return undefined;
    }

    setProfile(session.user);
    loadShipments();

    const refreshTimer = window.setInterval(loadShipments, 30000);

    return () => {
      window.clearInterval(refreshTimer);
    };
  }, [session, archived]);

  async function loadShipments() {
    setLoading(true);

    try {
      setShipments(await ovhApi.shipments({ archived }));
    } catch (error) {
      setNotice(error.message);
    }

    setLoading(false);
  }

  const isAdmin = profile?.role === 'admin';
  const isStaff =
    profile?.role === 'admin' || profile?.role === 'logistics';

  const filtered = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return shipments
      .filter((shipment) => {
        const haystack = [
          shipment.customer_code,
          shipment.recipient,
          shipment.city,
          shipment.ldv_number,
          shipment.bl_number,
          shipment.stef_code,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return (
          (!normalizedSearch ||
            haystack.includes(normalizedSearch)) &&
          (!commercial ||
            shipment.commercial_code === commercial) &&
          (!sector || shipment.sector === sector)
        );
      })
      .sort((first, second) => {
        const rankDifference =
          statusRank(first) - statusRank(second);

        if (rankDifference !== 0) return rankDifference;

        const firstStatus = computedStatus(first);
        const secondStatus = computedStatus(second);

        if (
          firstStatus.key === 'late' &&
          secondStatus.key === 'late'
        ) {
          return secondStatus.delayDays - firstStatus.delayDays;
        }

        const firstDate =
          first.estimated_delivery_date ||
          first.shipping_date ||
          '';

        const secondDate =
          second.estimated_delivery_date ||
          second.shipping_date ||
          '';

        return String(firstDate).localeCompare(String(secondDate));
      });
  }, [shipments, search, commercial, sector]);

  const counters = useMemo(() => {
    return shipments.reduce(
      (accumulator, shipment) => {
        const key = computedStatus(shipment).key;

        accumulator.total += 1;
        accumulator[key] += 1;

        return accumulator;
      },
      {
        total: 0,
        pending_preparation: 0,
        in_delivery: 0,
        late: 0,
        completed: 0,
      }
    );
  }, [shipments]);

  const commercials = useMemo(() => {
    return [
      ...new Set(
        shipments
          .map((shipment) => shipment.commercial_code)
          .filter(Boolean)
      ),
    ].sort();
  }, [shipments]);

  const sectors = useMemo(() => {
    return [
      ...new Set(
        shipments
          .map((shipment) => shipment.sector)
          .filter(Boolean)
      ),
    ].sort();
  }, [shipments]);

  const selectableShipmentIds = useMemo(
    () => (!archived ? filtered.map((shipment) => shipment.id) : []),
    [archived, filtered]
  );

  const selectedCount = selectedShipmentIds.size;
  const allVisibleSelected =
    selectableShipmentIds.length > 0 &&
    selectableShipmentIds.every((id) => selectedShipmentIds.has(id));

  function toggleShipmentSelection(id) {
    setSelectedShipmentIds((current) => {
      const next = new Set(current);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  function toggleAllVisibleShipments() {
    setSelectedShipmentIds((current) => {
      const next = new Set(current);

      if (allVisibleSelected) {
        selectableShipmentIds.forEach((id) => next.delete(id));
      } else {
        selectableShipmentIds.forEach((id) => next.add(id));
      }

      return next;
    });
  }

  async function processShipmentSelection(action) {
    const ids = [...selectedShipmentIds];
    if (!ids.length) return;

    const deleting = action === 'delete';
    const confirmed = window.confirm(
      deleting
        ? `Supprimer définitivement ${ids.length} expédition(s) ? Cette action est irréversible.`
        : `Archiver maintenant ${ids.length} expédition(s) ?`
    );

    if (!confirmed) return;

    try {
      await ovhApi.bulkShipments(
        deleting ? 'delete' : 'archive',
        ids
      );
    } catch (error) {
      setNotice(`Action impossible : ${error.message}`);
      return;
    }

    setSelectedShipmentIds(new Set());
    setNotice(
      deleting
        ? `${ids.length} expédition(s) supprimée(s) définitivement.`
        : `${ids.length} expédition(s) archivée(s).`
    );
    await loadShipments();
  }

  async function createPending({ customerCode, shippingDate }) {
    if (!customerCode || !shippingDate) {
      setNotice('Le code client et la date sont obligatoires.');
      return false;
    }

    setCreatingShipment(true);

    try {
      await ovhApi.createShipment({
        customer_code: customerCode,
        shipping_date: shippingDate,
        status: 'pending_preparation',
        source: 'planning',
      });
    } catch (error) {
      setCreatingShipment(false);
      setNotice(error.message);
      return false;
    }

    setCreatingShipment(false);
    setNotice('L’expédition en attente a été créée.');
    await loadShipments();

    return true;
  }

 async function importPdf(file) {
  try {
    setNotice('Lecture du bordereau de livraison en cours…');

    const {
      shipments: parsedShipments,
      expectedPositionCount,
    } = await parseStefPdf(file);

    let plannedShipmentsUpdated = 0;
    let newShipmentsCreated = 0;
    let existingShipmentsIgnored = 0;
    let failedShipments = 0;

    const errorDetails = [];

    for (const parsedShipment of parsedShipments) {
      try {
        /*
         * Une LDV identique signifie que cette livraison a
         * normalement déjà été importée.
         */
        const existingShipments = shipments.filter(
          (shipment) =>
            matchesImportedLdv(
              shipment.ldv_number,
              parsedShipment.ldv_number
            )
        );

        if (existingShipments?.length) {
          await ovhApi.updateShipment(existingShipments[0].id, {
            customer_code: parsedShipment.customer_code,
            stef_code: parsedShipment.stef_code,
            recipient: parsedShipment.recipient,
            city: parsedShipment.city,
            pallet_count: parsedShipment.pallet_count,
            parcel_count: parsedShipment.parcel_count,
            weight_kg: parsedShipment.weight_kg,
            bl_number: parsedShipment.bl_number,
            ldv_number: parsedShipment.ldv_number,
            shipping_date: parsedShipment.shipping_date,
            estimated_delivery_date:
              parsedShipment.estimated_delivery_date,
          });

          existingShipmentsIgnored += 1;
          continue;
        }

        /*
         * Recherche d'une expédition préalablement planifiée.
         */
        const plannedMatches = shipments.filter(
          (shipment) =>
            shipment.customer_code === parsedShipment.customer_code &&
            shipment.shipping_date === parsedShipment.shipping_date &&
            shipment.status === 'pending_preparation' &&
            !shipment.archived_at
        );

        if ((plannedMatches || []).length > 1) {
          throw new Error(
            `Plusieurs expéditions en attente correspondent au code ${parsedShipment.customer_code} pour le ${formatDate(parsedShipment.shipping_date)}.`
          );
        }

        /*
         * Seulement les colonnes présentes dans la table
         * Supabase sont envoyées.
         */
        const payload = {
          customer_code:
            parsedShipment.customer_code,
          stef_code: parsedShipment.stef_code,
          recipient: parsedShipment.recipient,
          city: parsedShipment.city,
          pallet_count:parsedShipment.pallet_count,
          parcel_count: parsedShipment.parcel_count,
          weight_kg: parsedShipment.weight_kg,
          bl_number: parsedShipment.bl_number,
          ldv_number: parsedShipment.ldv_number,
          shipping_date:
            parsedShipment.shipping_date,
          estimated_delivery_date:
            parsedShipment.estimated_delivery_date,
          status: 'in_delivery',
          source: 'pdf',
        };

        if ((plannedMatches || []).length === 1) {
          await ovhApi.updateShipment(plannedMatches[0].id, payload);

          plannedShipmentsUpdated += 1;
        } else {
          await ovhApi.createShipment(payload);

          newShipmentsCreated += 1;
        }
      } catch (shipmentError) {
        failedShipments += 1;

        errorDetails.push(
          `${
            parsedShipment.customer_code ||
            parsedShipment.ldv_number ||
            'Livraison non identifiée'
          } : ${shipmentError.message}`
        );
      }
    }

    const summary = [
      `${parsedShipments.length} livraison(s) reconnue(s)`,
      `${plannedShipmentsUpdated} expédition(s) planifiée(s) mise(s) en livraison`,
      `${newShipmentsCreated} nouvelle(s) expédition(s) créée(s)`,
    ];

    if (existingShipmentsIgnored) {
      summary.push(
        `${existingShipmentsIgnored} livraison(s) déjà présente(s)`
      );
    }

    if (failedShipments) {
      summary.push(
        `${failedShipments} erreur(s) d’enregistrement`
      );
    }

    if (expectedPositionCount !== null) {
      summary.unshift(
        `${expectedPositionCount} position(s) annoncée(s)`
      );
    }

    setNotice(summary.join(' · '));

    if (errorDetails.length) {
      console.error(
        'Erreurs d’import du bordereau STEF :',
        errorDetails
      );
    }

    await loadShipments();
  } catch (error) {
    setNotice(`Import PDF annulé : ${error.message}`);
  }
}

  async function applyPodToShipment({
    shipment,
    pod,
    documentId,
  }) {
    const conformity = pod.has_reservations
      ? 'non_compliant'
      : 'compliant';
    await ovhApi.applyPod({
      shipment_id: shipment.id,
      document_id: documentId,
      new_ldv: pod.ldv_number,
      raw_ldv: pod.ldv_raw,
      actual_delivery_date: pod.actual_delivery_date,
      conformity,
    });

    return conformity;
  }

  async function registerPodDocument(file, pod) {
    const sha256 = await sha256File(file);
    const payload = {
      sha256,
      file_name: file.name,
      mime_type: file.type || 'application/pdf',
      file_size: file.size,
      ldv_raw: pod.ldv_raw,
      ldv_normalized: pod.ldv_number,
      bl_number: pod.bl_number,
      stef_code: pod.stef_code || null,
      recipient: pod.recipient,
      city: pod.city,
      pallet_count: pod.pallet_count,
      parcel_count: pod.parcel_count,
      weight_kg: pod.weight_kg,
      remise_date: pod.remise_date,
      actual_delivery_date: pod.actual_delivery_date,
      actual_delivery_time: pod.actual_delivery_time,
      signer_name: pod.signer_name,
      signed_good_condition: pod.signed_good_condition,
      has_reservations: pod.has_reservations,
      parser_data: pod,
    };
    return ovhApi.registerPod(payload);
  }

  async function findPodExactShipment(pod) {
    return (
      shipments.find(
        (shipment) =>
          shipment.ldv_number === pod.ldv_number ||
          (shipment.ldv_aliases || []).includes(pod.ldv_number)
      ) || null
    );
  }

  async function createPodReview(documentId, candidate, pod) {
    await ovhApi.createPodReview({
      document_id: documentId,
      shipment_id: candidate?.shipment.id || null,
      score: candidate?.score || 0,
      matching_reasons: candidate?.reasons || [],
      conflicts: candidate?.conflicts || [],
    });

    if (!candidate) return;

    setLdvSuggestions((current) => [
      ...current,
      {
        ...candidate,
        source: 'pod',
        documentId,
        pod,
        row: {
          ldv: pod.ldv_number,
          shippingDate: pod.remise_date,
        },
      },
    ]);
  }

  async function testPod(file) {
    try {
      setNotice('Lecture du POD en cours…');

      const pod = await parsePodPdf(file);
      const { document, duplicate } = await registerPodDocument(file, pod);

      if (duplicate && document.status === 'matched') {
        setNotice(
          `Ce POD a déjà été traité pour la LDV ${pod.ldv_number}.`
        );
        return;
      }

      const exactShipment = await findPodExactShipment(pod);

      if (exactShipment) {
        const conformity = await applyPodToShipment({
          shipment: exactShipment,
          pod,
          documentId: document.id,
        });

        setNotice(
          `POD reconnu par LDV · Livraison terminée le ${formatDate(
            pod.actual_delivery_date
          )} · ${
            conformity === 'compliant' ? 'Conforme' : 'Non conforme'
          }`
        );
        await loadShipments();
        return;
      }

      const possibleShipments = shipments.filter(
        (shipment) =>
          shipment.status === 'in_delivery' &&
          !shipment.archived_at
      );

      const ranked = (possibleShipments || [])
        .map((shipment) => ({
          shipment,
          ...scorePodCandidate(shipment, pod),
        }))
        .sort((first, second) => second.score - first.score);
      const best = ranked[0];
      const second = ranked[1];
      const uniqueAutomaticMatch =
        best?.automatic &&
        (!second || !second.automatic || best.score > second.score);

      if (uniqueAutomaticMatch) {
        await applyPodToShipment({
          shipment: best.shipment,
          pod,
          documentId: document.id,
        });
        setNotice(
          `Nouvelle LDV rapprochée automatiquement : ${best.shipment.ldv_number} → ${pod.ldv_number} · Livraison terminée.`
        );
        await loadShipments();
        return;
      }

      const reviewCandidate = best?.score >= 8 ? best : null;
      await createPodReview(document.id, reviewCandidate, pod);

      setNotice(
        reviewCandidate
          ? 'Le POD nécessite une confirmation avant de modifier la livraison.'
          : `Aucune expédition suffisamment proche de la LDV ${pod.ldv_number}. Le POD est placé à vérifier.`
      );
    } catch (error) {
      setNotice(`Test du POD annulé : ${error.message}`);
    }
  }

  async function importTrack(file) {
    try {
      setNotice('Lecture du Track & Trace en cours…');
      setLdvSuggestions([]);

      const rows = await parseTrackAndTrace(file);

      let updated = 0;
      let missing = 0;
      const suggestions = [];

      for (const row of rows) {
        const exactMatches = shipments.filter(
          (shipment) => shipment.ldv_number === row.ldv
        );

        if (exactMatches?.length) {
          const payload = buildTrackPayload(
            row,
            exactMatches[0].completed_at
          );

          await ovhApi.updateShipment(exactMatches[0].id, payload);

          updated += 1;
          continue;
        }

        if (!row.blNumber || !row.stefCode) {
          missing += 1;
          continue;
        }

        const possibleMatches = shipments.filter(
          (shipment) =>
            shipment.bl_number === row.blNumber &&
            shipment.stef_code === row.stefCode &&
            !shipment.archived_at
        );

        const rankedMatches = (possibleMatches || [])
          .filter((shipment) => shipment.ldv_number !== row.ldv)
          .map((shipment) => ({
            shipment,
            row,
            ...scoreLdvCandidate(shipment, row),
          }))
          .sort((first, second) => second.score - first.score);

        const bestMatch = rankedMatches[0];
        const secondMatch = rankedMatches[1];

        /*
         * BL + code STEF sont obligatoires. En cas de plusieurs candidats,
         * la meilleure proposition doit aussi avoir un score strictement
         * supérieur à la suivante.
         */
        if (
          bestMatch &&
          bestMatch.score >= 6 &&
          (!secondMatch || bestMatch.score > secondMatch.score)
        ) {
          suggestions.push(bestMatch);
        } else {
          missing += 1;
        }
      }

      setLdvSuggestions(suggestions);

      const summary = [
        `${updated} expédition(s) mise(s) à jour`,
      ];

      if (suggestions.length) {
        summary.push(
          `${suggestions.length} changement(s) de LDV à vérifier`
        );
      }

      if (missing) {
        summary.push(
          `${missing} LDV sans correspondance`
        );
      }

      setNotice(summary.join(' · '));
      await loadShipments();
    } catch (error) {
      setNotice(`Import Track & Trace annulé : ${error.message}`);
    }
  }

  async function confirmLdvChange(suggestion) {
    setResolvingLdvChange(true);

    try {
      if (suggestion.source === 'pod') {
        await applyPodToShipment({
          shipment: suggestion.shipment,
          pod: suggestion.pod,
          documentId: suggestion.documentId,
        });

        await ovhApi.decidePodReview({
          status: 'confirmed',
          document_id: suggestion.documentId,
          shipment_id: suggestion.shipment.id,
        });

        setLdvSuggestions((current) => current.slice(1));
        setNotice(
          `POD confirmé : ${suggestion.shipment.ldv_number} → ${suggestion.pod.ldv_number} · Livraison terminée.`
        );
        await loadShipments();
        return;
      }

      const { shipment, row } = suggestion;
      const aliases = [
        ...new Set([
          ...(shipment.ldv_aliases || []),
          shipment.ldv_number,
        ].filter(Boolean)),
      ];

      const payload = {
        ...buildTrackPayload(row, shipment.completed_at),
        ldv_aliases: aliases,
        initial_shipping_date:
          shipment.initial_shipping_date ||
          shipment.shipping_date ||
          null,
        ldv_number: row.ldv,
        shipping_date:
          row.shippingDate || shipment.shipping_date,
        estimated_delivery_date:
          row.estimatedDeliveryDate ||
          shipment.estimated_delivery_date,
        ldv_changed_at: new Date().toISOString(),
      };

      await ovhApi.updateShipment(shipment.id, payload);

      setLdvSuggestions((current) => current.slice(1));
      setNotice(
        `LDV mise à jour : ${shipment.ldv_number} → ${row.ldv}.`
      );
      await loadShipments();
    } catch (error) {
      setNotice(`Changement de LDV impossible : ${error.message}`);
    } finally {
      setResolvingLdvChange(false);
    }
  }

  async function ignoreLdvChange() {
    const suggestion = ldvSuggestions[0];

    if (suggestion?.source === 'pod') {
      try {
        await ovhApi.decidePodReview({
          status: 'ignored',
          document_id: suggestion.documentId,
          shipment_id: suggestion.shipment.id,
        });
      } catch (error) {
        setNotice(`Impossible d'ignorer la proposition : ${error.message}`);
        return;
      }
    }

    setLdvSuggestions((current) => current.slice(1));
    setNotice(
      suggestion?.source === 'pod'
        ? 'Proposition POD ignorée. Le document reste à vérifier.'
        : 'Proposition de changement de LDV ignorée.'
    );
  }

  async function removeShipment(id) {
    const confirmed = window.confirm(
      'Cette suppression est définitive. Voulez-vous continuer ?'
    );

    if (!confirmed) return;

    try {
      await ovhApi.deleteShipment(id);
    } catch (error) {
      setNotice(error.message);
      return;
    }

    setNotice('L’expédition a été supprimée définitivement.');
    await loadShipments();
  }

  if (!sessionChecked) {
    return (
      <div className="application-loading">
        Chargement de l’application…
      </div>
    );
  }

  if (passwordSetup) {
    return (
      <RecoveryPasswordPage
        type={passwordSetup.type}
        token={passwordSetup.token}
        onAuthenticated={(user) => {
          setSession({ user });
          setPasswordSetup(null);
        }}
        onCancel={() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          setPasswordSetup(null);
        }}
      />
    );
  }

  if (!session) {
    return (
      <Login onLogin={(user) => setSession({ user })} />
    );
  }

  return (
    <div className="app">
      <header>
        <div className="header-identity">
          <h1>Suivi des expéditions</h1>

          <span>
            {profile?.full_name || session.user.email}
            {isAdmin ? ' · Administrateur' : ''}
            {profile?.role === 'logistics' ? ' · Logistique' : ''}
          </span>
        </div>

        <div className="header-actions">
          {isStaff && (
            <button
              type="button"
              className="header-logout"
              onClick={() => setUsersView(true)}
              title="Utilisateurs"
              aria-label="Ouvrir la gestion des utilisateurs"
            >
              <Users size={17} />
              Utilisateurs
            </button>
          )}

          <button
            type="button"
            className="header-logout"
            onClick={() => setChangePasswordOpen(true)}
            title="Modifier mon mot de passe"
            aria-label="Modifier mon mot de passe"
          >
            <KeyRound size={17} />
            Modifier mon mot de passe
          </button>

          <button
            type="button"
            className="header-logout"
            title="Déconnexion"
            aria-label="Se déconnecter"
            onClick={async () => {
              try {
                await ovhApi.logout();
              } finally {
                setSession(null);
              }
            }}
          >
            <LogOut size={17} />
            Déconnexion
          </button>
        </div>
      </header>

      <main>
        {notice && (
          <div className="notice">
            <span>{notice}</span>

            <button
              type="button"
              onClick={() => setNotice('')}
              aria-label="Fermer le message"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {usersView && isStaff ? (
          <UserManagement
            currentUserId={session.user.id}
            currentRole={profile?.role}
            onBack={() => setUsersView(false)}
            onNotice={setNotice}
          />
        ) : (
          <>
        <section className="stats">
          <StatCard
            value={counters.total}
            label="Total"
            statusClass="stat-total"
          />

          <StatCard
            value={counters.pending_preparation}
            label="En attente de préparation"
            statusClass="stat-pending"
          />

          <StatCard
            value={counters.in_delivery}
            label="En cours de livraison"
            statusClass="stat-progress"
          />

          <StatCard
            value={counters.late}
            label="En retard"
            statusClass="stat-late"
            active={counters.late > 0}
          />

          <StatCard
            value={counters.completed}
            label="Livraisons terminées"
            statusClass="stat-completed"
          />
        </section>

        <section className="toolbar panel">
          <div className="search">
            <Search size={18} />

            <input
              type="search"
              placeholder="Rechercher un client, une ville, une LDV, un BL…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <select
            value={commercial}
            onChange={(event) =>
              setCommercial(event.target.value)
            }
          >
            <option value="">Tous les commerciaux</option>

            {commercials.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <select
            value={sector}
            onChange={(event) => setSector(event.target.value)}
          >
            <option value="">Tous les secteurs</option>

            {sectors.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="icon-button refresh-button"
            onClick={loadShipments}
            title="Actualiser"
          >
            <RefreshCw
              size={17}
              className={loading ? 'spinning' : ''}
            />
          </button>
        </section>

        {isStaff && (
          <section className="admin panel">
            <button
              type="button"
              onClick={() => setCreateModalOpen(true)}
            >
              <PackagePlus size={18} />
              Planifier une expédition
            </button>

            <label className="file-btn">
              <FileText size={18} />
              Importer un Bordereau

              <input
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0];

                  if (file) importPdf(file);

                  event.target.value = '';
                }}
              />
            </label>

            <label className="file-btn">
              <FileSpreadsheet size={18} />
              Importer le Track & Trace

              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(event) => {
                  const file = event.target.files?.[0];

                  if (file) importTrack(file);

                  event.target.value = '';
                }}
              />
            </label>

            <label className="file-btn pod-test-btn">
              <FileCheck2 size={18} />
              Tester un POD

              <input
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0];

                  if (file) testPod(file);

                  event.target.value = '';
                }}
              />
            </label>

            <div className="archive-switch">
              <button
                type="button"
                className={!archived ? 'selected' : ''}
                onClick={() => setArchived(false)}
              >
                Expéditions actives
              </button>

              <button
                type="button"
                className={archived ? 'selected' : ''}
                onClick={() => setArchived(true)}
              >
                <Archive size={17} />
                Archives
              </button>
            </div>
          </section>
        )}

        <section className="panel table-wrap">
          <div className="table-heading">
            <div>
              <h2>
                {archived
                  ? 'Expéditions archivées'
                  : 'Expéditions actives'}
              </h2>

              <span>
                {filtered.length} résultat
                {filtered.length > 1 ? 's' : ''}
              </span>
            </div>

            <span className="table-scroll-hint">
              Faites glisser le tableau horizontalement pour voir la suite.
            </span>

            {isStaff && !archived && (
              <div className="bulk-actions">
                <strong>{selectedCount} sélectionnée(s)</strong>

                <button
                  type="button"
                  className="secondary"
                  disabled={!selectedCount}
                  onClick={() => processShipmentSelection('archive')}
                >
                  <Archive size={16} />
                  Archiver
                </button>

                <button
                  type="button"
                  className="danger-text"
                  disabled={!selectedCount}
                  onClick={() => processShipmentSelection('delete')}
                >
                  <Trash2 size={16} />
                  Supprimer
                </button>
              </div>
            )}
          </div>

          {isStaff && !archived && filtered.length > 0 && (
            <label className="mobile-select-all">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleAllVisibleShipments}
              />
              Tout sélectionner ({filtered.length})
            </label>
          )}

          <div className="mobile-shipments">
            {filtered.map((shipment) => (
              <MobileShipmentCard
                key={shipment.id}
                shipment={shipment}
                isStaff={isStaff}
                archived={archived}
                selected={selectedShipmentIds.has(shipment.id)}
                onToggle={() => toggleShipmentSelection(shipment.id)}
                onRemove={() => removeShipment(shipment.id)}
              />
            ))}

            {!loading && !filtered.length && (
              <div className="empty mobile-empty">
                <strong>Aucune expédition à afficher</strong>
                <span>
                  Les expéditions correspondant à cette sélection
                  apparaîtront ici.
                </span>
              </div>
            )}
          </div>

          <div className="table-scroll shipments-table-scroll">
            <table className="shipment-table">
              <thead>
                <tr>
                  {isStaff && !archived && (
                    <th className="selection-cell">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisibleShipments}
                        aria-label="Tout sélectionner"
                      />
                    </th>
                  )}
                  <th>Statut</th>
                  <th>Code client</th>
                  <th>Destinataire</th>
                  <th>Ville</th>
                  <th>Palettes</th>
                  <th>BL</th>
                  <th>LDV</th>
                  <th>Expédition</th>
                  <th>Date de livraison</th>
                  <th>Conformité</th>

                  {isStaff && <th aria-label="Actions" />}
                </tr>
              </thead>

              <tbody>
                {filtered.map((shipment) => (
                  <tr key={shipment.id}>
                    {isStaff && !archived && (
                      <td className="selection-cell" data-label="Sélection">
                        <input
                          type="checkbox"
                          checked={selectedShipmentIds.has(shipment.id)}
                          onChange={() =>
                            toggleShipmentSelection(shipment.id)
                          }
                          aria-label={`Sélectionner ${safe(
                            shipment.customer_code
                          )}`}
                        />
                      </td>
                    )}
                    <td className="status-cell" data-label="Statut">
                      <Badge shipment={shipment} />
                    </td>

                    <td className="customer-code" data-label="Code client">
                      {safe(shipment.customer_code)}
                    </td>

                    <td className="recipient-cell" data-label="Destinataire">
                      {safe(shipment.recipient)}
                    </td>
                    <td data-label="Ville">{safe(shipment.city)}</td>
                    <td data-label="Palettes">{safe(shipment.pallet_count)}</td>
                    <td data-label="BL">{safe(shipment.bl_number)}</td>
                    <td data-label="LDV">{safe(shipment.ldv_number)}</td>
                    <td data-label="Expédition">
                      {formatDate(shipment.shipping_date)}
                    </td>

                    <td className="delivery-cell" data-label="Date de livraison">
                      {formatDeliveryDate(shipment)}
                    </td>

                    <td data-label="Conformité">
                      <ConformityBadge
                        value={shipment.conformity}
                      />
                    </td>

                    {isStaff && (
                      <td className="action-cell" data-label="Action">
                        <button
                          type="button"
                          className="danger"
                          onClick={() =>
                            removeShipment(shipment.id)
                          }
                          title="Supprimer définitivement"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {!loading && !filtered.length && (
              <div className="empty">
                <strong>Aucune expédition à afficher</strong>

                <span>
                  Les expéditions correspondant à cette sélection
                  apparaîtront ici.
                </span>
              </div>
            )}

            {loading && (
              <div className="empty">
                <span>Chargement des expéditions…</span>
              </div>
            )}
          </div>
        </section>
          </>
        )}
      </main>

      <CreateShipmentModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={createPending}
        submitting={creatingShipment}
      />

      <LdvChangeModal
        suggestions={ldvSuggestions}
        resolving={resolvingLdvChange}
        onConfirm={confirmLdvChange}
        onIgnore={ignoreLdvChange}
      />

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSuccess={setNotice}
      />
    </div>
  );
}

export default App;
